import { UserService } from '../../domain-services/User.service';
import type { ModalAction } from '../../interfaces/ModalAction';
import type { MyGoogleUserData } from '../../interfaces/MyGoogleUserData';
import { StatusEnum } from '../../interfaces/StatusEnum';
import { UserRoleEnum } from '../../interfaces/UserRoleEnum';
import { RelationsGoogleUserEnum } from '../../interfaces/RelationsGoogleUserEnum';

export function enhanceMyGoogleUserDatasWithStatus(
	MyGoogleUserDatas: MyGoogleUserData[]
): MyGoogleUserData[] {
	const today = new Date();
	return MyGoogleUserDatas?.map((MyGoogleUserData: MyGoogleUserData) => {
		const expirationRaw =
			MyGoogleUserData.expirationDate ??
			MyGoogleUserData.customSchemas?.admin_management_attributes?.expirationDate ??
			'';
		const expirationDate = new Date(expirationRaw);
		MyGoogleUserData.expirationDateFormat = expirationDate.toLocaleDateString('fr');
		if (expirationDate < today) {
			MyGoogleUserData.status = StatusEnum.Expired;
		}
		const diffInTime = expirationDate.getTime() - today.getTime();
		MyGoogleUserData.daysBeforeExpiration = Math.round(diffInTime / (1000 * 3600 * 24));
		if (MyGoogleUserData.daysBeforeExpiration <= 30 && MyGoogleUserData.daysBeforeExpiration > -1) {
			MyGoogleUserData.status = StatusEnum.Warning;
		} else if (MyGoogleUserData.daysBeforeExpiration >= 30) {
			MyGoogleUserData.status = StatusEnum.Updated;
		}
		return MyGoogleUserData;
	});
}

export function updateButtonsStatus(
	cancelButton?: ModalAction,
	submitButton?: ModalAction,
	status?: boolean
) {
	if (cancelButton) {
		cancelButton.isDisabled = status;
	}
	if (submitButton) {
		submitButton.isLoading = status;
	}
}

export function formatEmail(input: string) {
	const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
	if (emailPattern.test(input)) {
		return input.toLowerCase();
	}
	if (import.meta.env.VITE_ENVIRONMENT === 'PREPROD') {
		return `${input.toLowerCase()}@test.oxylane.com`;
	} else {
		return `${input.toLowerCase()}@decathlon.net`;
	}
}

export function transformEmail(email: string): string {
	if (!email.includes('@')) {
		return email;
	}
	const userNameWithSpaces = email.split('@')[0].replaceAll('.', ' ');
	const nameWithoutDigits = userNameWithSpaces.replaceAll(/\d/g, '');
	const completeName = nameWithoutDigits.replaceAll(/partner/gi, '').trim();
	const capitalizedCompleteName = completeName
		.split(' ')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');

	return capitalizedCompleteName;
}

export function decodeJWT(token: string) {
	if (!token) {
		throw new Error('Token is empty');
	}
	const parts = token.split('.');
	if (parts.length < 2) {
		throw new Error('Invalid JWT token');
	}
	const [header, payload] = parts;

	const base64UrlDecode = (str: string) => {
		let output = str.replaceAll('-', '+').replaceAll('_', '/');
		switch (output.length % 4) {
			case 0:
				break;
			case 2:
				output += '==';
				break;
			case 3:
				output += '=';
				break;
			default:
				throw new Error('Illegal base64url string!');
		}
		return atob(output);
	};

	const decodedHeader = JSON.parse(base64UrlDecode(header));
	const decodedPayload = JSON.parse(base64UrlDecode(payload));
	return { header: decodedHeader, payload: decodedPayload };
}

export function isTokenExpired(token: string) {
	try {
		const decodedToken = decodeJWT(token);
		const now = Math.floor(Date.now() / 1000);
		return decodedToken?.payload?.exp < now;
	} catch (error) {
		console.warn('Failed to decode JWT token:', error);
		return true;
	}
}

export function areArraysEqual(arr1: string[] = [], arr2: string[] = []) {
	if (!arr1 || !arr2) return false;
	if (arr1.length !== arr2.length) return false;
	const sortedArr1 = [...arr1].sort((a, b) => a.localeCompare(b));
	const sortedArr2 = [...arr2].sort((a, b) => a.localeCompare(b));
	return sortedArr1.every((value, index) => value === sortedArr2[index]);
}

export function formatFullname(value: string): string {
	const parts = value.trim().split(' ');

	if (parts.length < 2) {
		return value;
	}

	// Format the first name(s)
	const firstNames = parts[0]
		.split('-')
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
		.join('-');

	// Convert everything after the first name to uppercase
	const secondName = parts.slice(1).join(' ').toUpperCase();

	return `${firstNames} ${secondName}`;
}

export function formatFileSize(bytes: number): string {
	if (bytes === 0) return '0 Bytes';
	const k = 1024;
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export async function checkUserRole(
	feature?: 'calendar-admin' | 'building' | 'resource' | 'account-health' | 'data-extraction'
): Promise<UserRoleEnum> {
	const userService = new UserService();

	const data = (await userService.getUserRights()) ?? [];

	const hasAdminRole = data.some((role) => role.category === UserRoleEnum.Admin);
	const hasSupportRole = data.some((role) => role.category === UserRoleEnum.Support);
	const hasSiteManagerRole = data.some((role) => role.category === UserRoleEnum.SiteManager);
	const hasCommunicatorRole = data.some((role) => role.category === UserRoleEnum.Communicator);

	if (hasAdminRole) {
		return UserRoleEnum.Admin;
	}

	if (
		feature === 'building' ||
		feature === 'resource' ||
		feature === 'calendar-admin' ||
		feature === 'data-extraction'
	) {
		if (hasSiteManagerRole) {
			return UserRoleEnum.SiteManager;
		}
		if (hasSupportRole) {
			return UserRoleEnum.Support;
		}
	}

	if (feature === 'account-health') {
		if (hasSupportRole) {
			return UserRoleEnum.Support;
		}
		if (hasSiteManagerRole) {
			return UserRoleEnum.SiteManager;
		}
	}

	if (hasSiteManagerRole) {
		return UserRoleEnum.SiteManager;
	}
	if (hasSupportRole) {
		return UserRoleEnum.Support;
	}
	if (hasCommunicatorRole) {
		return UserRoleEnum.Communicator;
	}

	return UserRoleEnum.Teammate;
}

export async function userHasAnyRole(roles: UserRoleEnum[] = []): Promise<boolean> {
	const userService = new UserService();
	const data = (await userService.getUserRights()) ?? [];
	if (!roles || roles.length === 0) return false;
	return data.some((role) => roles.includes(role.category as UserRoleEnum));
}

export function chunkArray<T>(arr: T[], size: number): T[][] {
	const chunks: T[][] = [];
	for (let i = 0; i < arr.length; i += size) {
		chunks.push(arr.slice(i, i + size));
	}
	return chunks;
}

export function parseContentRange(contentRange?: string) {
	if (!contentRange) return null;
	const contentRangeRegex = /(?:items\s+)?(\d+)-(\d+)\/(\d+|\*)/i;
	const match = contentRangeRegex.exec(contentRange);
	if (!match) return null;

	const start = Number.parseInt(match[1], 10);
	const end = Number.parseInt(match[2], 10);
	const total = match[3] === '*' ? null : Number.parseInt(match[3], 10);

	if (!Number.isFinite(start) || !Number.isFinite(end)) {
		return null;
	}

	return { start, end, total };
}

export async function enrichSharedMailboxesWithManagers(
	mailboxes: MyGoogleUserData[],
	userService: UserService
): Promise<void> {
	if (!mailboxes || mailboxes.length === 0) return;

	const managerEmails = mailboxes
		.map((sm) => sm.relations?.find((r) => r?.type === RelationsGoogleUserEnum.Manager)?.value)
		.filter((email): email is string => Boolean(email))
		.map((email) => email.toLowerCase());

	const uniqueManagerEmails = Array.from(new Set(managerEmails));

	if (uniqueManagerEmails.length === 0) return;

	try {
		const managers = await userService.getUsersByEmails(uniqueManagerEmails);
		const managerMap = new Map(managers.map((m) => [m.primaryEmail?.toLowerCase(), m]));

		mailboxes.forEach((mb) => {
			const managerEmail = mb.relations?.find(
				(r) => r?.type === RelationsGoogleUserEnum.Manager
			)?.value;
			if (managerEmail && managerMap.has(managerEmail.toLowerCase())) {
				mb.manager = managerMap.get(managerEmail.toLowerCase());
			}
		});
	} catch (error) {
		console.warn('Failed to fetch managers for shared mailboxes:', error);
	}
}
